import { useState, useEffect, ReactNode, FormEvent } from 'react'
import { toast } from 'sonner'
import { Pencil, MoreVertical } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

// ───────────────────────────────────────────────────────────────────────
// Shared inline-edit + drawer primitives for Record Pages.
//
// Pattern: detail pages render values as text at rest. Click a value →
// small Popover with the relevant editor (text / number / date / select /
// status chip). Save on Enter/click. Toast feedback via `sonner`.
//
// Multi-field edits use <RecordDrawer> (right-side Sheet).
// Delete confirmation uses useDeleteConfirm() (AlertDialog).
//
// Reference impl: ConsultingProjectDetail.tsx.
// Sister memory: project_mooderp_detail_pattern.md.
// ───────────────────────────────────────────────────────────────────────

// Sentinel for "no selection" in optional Radix Selects. Radix Select
// reserves the empty string as its internal "no value" state, so we use
// this placeholder and translate it on read/write.
export const NONE = '__none__'

// Best-effort error message extractor for DRF responses.
export function extractErr(e: any): string {
  const data = e?.response?.data
  if (typeof data === 'string') return data
  if (data?.detail) return String(data.detail)
  if (data && typeof data === 'object') {
    const first = Object.entries(data)[0]
    if (first) return `${first[0]}: ${Array.isArray(first[1]) ? first[1][0] : first[1]}`
  }
  return e?.message || 'Save failed'
}

// Em-dash for empty/null values in read mode.
export function Dash() {
  return <span className="text-muted-foreground">—</span>
}

// Format a Date as a local YYYY-MM-DD. Avoids the off-by-one bug that
// hits users east of UTC when using `d.toISOString().slice(0,10)`.
export function localISODate(d: Date): string {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

// Visual wrapper that makes a value look like plain text at rest and
// reveals a pencil on hover. Used internally by every Inline* component.
export function EditTrigger({
  children, className = '', disabled,
}: { children: ReactNode; className?: string; disabled?: boolean }) {
  if (disabled) return <span className={className}>{children}</span>
  return (
    <span
      className={
        'group inline-flex items-center gap-1.5 rounded px-1 -mx-1 ' +
        'hover:bg-muted/60 cursor-text text-left ' + className
      }
    >
      <span className="flex-1 min-w-0">{children}</span>
      <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
    </span>
  )
}

// ─────────────── InlineText ───────────────
// Single-line input or textarea, depending on `multiline`.

export function InlineText({
  value, onSave, placeholder, multiline, helper, className,
}: {
  value: string
  onSave: (v: string) => Promise<void>
  placeholder?: string
  multiline?: boolean
  helper?: string
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)
  useEffect(() => { if (open) setDraft(value) }, [open, value])

  async function save() {
    if (draft === value) { setOpen(false); return }
    setSaving(true)
    try { await onSave(draft); toast.success('Saved'); setOpen(false) }
    catch (e) { toast.error(extractErr(e)) }
    finally { setSaving(false) }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="text-left w-full">
          <EditTrigger className={className}>
            {value || <Dash />}
          </EditTrigger>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={multiline ? 'w-96' : 'w-80'}
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {multiline ? (
          <Textarea
            autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder} rows={5}
          />
        ) : (
          <Input
            autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); save() }
              if (e.key === 'Escape') setOpen(false)
            }}
          />
        )}
        {helper && <p className="text-xs text-muted-foreground mt-2">{helper}</p>}
        <div className="flex justify-end gap-2 mt-3">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ─────────────── InlineNumber ───────────────
// Numeric input with optional suffix label and display formatting.

export function InlineNumber({
  value, onSave, placeholder, step = '0.01', suffix, helper,
  displayFormat, className, disabled,
}: {
  value: string
  onSave: (v: string) => Promise<void>
  placeholder?: string
  step?: string
  suffix?: string
  helper?: string
  displayFormat?: (v: string) => ReactNode
  className?: string
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)
  useEffect(() => { if (open) setDraft(value) }, [open, value])

  async function save() {
    if (draft === value) { setOpen(false); return }
    setSaving(true)
    try { await onSave(draft); toast.success('Saved'); setOpen(false) }
    catch (e) { toast.error(extractErr(e)) }
    finally { setSaving(false) }
  }

  const shown = displayFormat
    ? displayFormat(value)
    : (value !== '' && value != null
        ? <span className="font-mono">{value}{suffix ? ` ${suffix}` : ''}</span>
        : <Dash />)

  if (disabled) return <span className={className}>{shown}</span>

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="text-left">
          <EditTrigger className={className}>{shown}</EditTrigger>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Input
          autoFocus type="number" step={step}
          value={draft} onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          className="font-mono text-right"
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); save() }
            if (e.key === 'Escape') setOpen(false)
          }}
        />
        {suffix && <p className="text-xs text-muted-foreground mt-2">In {suffix}</p>}
        {helper && <p className="text-xs text-muted-foreground mt-2">{helper}</p>}
        <div className="flex justify-end gap-2 mt-3">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ─────────────── InlineDate ───────────────
// Date picker via shadcn Calendar. Saves YYYY-MM-DD in local time.

export function InlineDate({
  value, onSave, className,
  captionLayout, fromYear, toYear,
}: {
  value: string | null
  onSave: (v: string | null) => Promise<void>
  className?: string
  /**
   * Calendar header layout. Left undefined the calendar keeps its plain
   * "May 2026" label with prev/next arrows, which is fine for dates near
   * today. Pass ``'dropdown'`` for fields that routinely hold dates years
   * back (a company registration date, a birth date) so the year and month
   * can be picked directly instead of clicking back a month at a time.
   */
  captionLayout?: 'label' | 'dropdown' | 'dropdown-months' | 'dropdown-years'
  /** Lower bound for the year dropdown. Only meaningful with captionLayout. */
  fromYear?: number
  /** Upper bound for the year dropdown. Only meaningful with captionLayout. */
  toYear?: number
}) {
  const [open, setOpen] = useState(false)
  // Only bound the calendar when a caller opted into the dropdown header —
  // otherwise leave navigation unrestricted, as before.
  const startMonth = fromYear !== undefined ? new Date(fromYear, 0, 1) : undefined
  const endMonth = toYear !== undefined ? new Date(toYear, 11, 31) : undefined
  const selected = value ? new Date(value + 'T00:00:00') : undefined
  async function save(next: string | null) {
    if ((next || '') === (value || '')) { setOpen(false); return }
    try { await onSave(next); toast.success('Saved'); setOpen(false) }
    catch (e) { toast.error(extractErr(e)) }
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="text-left">
          <EditTrigger className={className}>
            {value || <Dash />}
          </EditTrigger>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          // Open on the stored date, not on today — otherwise a date years back
          // means scrolling all the way there again just to see it.
          defaultMonth={selected}
          captionLayout={captionLayout}
          startMonth={startMonth}
          endMonth={endMonth}
          onSelect={(d) => { if (d) save(localISODate(d)) }}
          autoFocus
        />
        {value && (
          <div className="p-2 border-t flex justify-end">
            <Button size="sm" variant="ghost" onClick={() => save(null)}>Clear</Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

// ─────────────── InlineSelect ───────────────
// Searchable picker (Command/cmdk) when options > 8, plain list otherwise.

export type InlineOption = {
  value: string
  label: ReactNode
  searchText?: string
  /** Arbitrary extra data callers can attach; not rendered by the picker. */
  meta?: Record<string, string>
}

export function InlineSelect({
  value, options, onSave, placeholder, helper, displayLabel,
  allowClear, className, disabled,
}: {
  value: string
  options: InlineOption[]
  onSave: (v: string) => Promise<void>
  placeholder?: string
  helper?: string
  displayLabel?: ReactNode
  allowClear?: boolean
  className?: string
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const useSearch = options.length > 8

  async function save(next: string) {
    if (next === value) { setOpen(false); return }
    try { await onSave(next); toast.success('Saved'); setOpen(false) }
    catch (e) { toast.error(extractErr(e)) }
  }

  const current = options.find((o) => o.value === value)
  const shown = displayLabel ?? (current ? current.label : (placeholder ? <span className="text-muted-foreground">{placeholder}</span> : <Dash />))

  if (disabled) return <span className={className}>{shown}</span>

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="text-left">
          <EditTrigger className={className}>{shown}</EditTrigger>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        {useSearch ? (
          <Command>
            <CommandInput placeholder="Search…" />
            <CommandList>
              <CommandEmpty>No matches.</CommandEmpty>
              <CommandGroup>
                {allowClear && (
                  <CommandItem value="__clear__" onSelect={() => save('')}>
                    <span className="text-muted-foreground">— none —</span>
                  </CommandItem>
                )}
                {options.map((o) => (
                  <CommandItem
                    key={o.value}
                    value={o.searchText || String(o.label)}
                    onSelect={() => save(o.value)}
                  >
                    {o.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        ) : (
          <div className="p-1 max-h-72 overflow-auto">
            {allowClear && (
              <button
                type="button"
                onClick={() => save('')}
                className="w-full text-left px-3 py-2 text-sm rounded hover:bg-muted text-muted-foreground"
              >— none —</button>
            )}
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => save(o.value)}
                className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-muted ${o.value === value ? 'bg-muted/60' : ''}`}
              >{o.label}</button>
            ))}
          </div>
        )}
        {helper && <p className="text-xs text-muted-foreground px-3 py-2 border-t">{helper}</p>}
      </PopoverContent>
    </Popover>
  )
}

// ─────────────── InlineTypeahead ───────────────
// Server-driven async picker. Fires `loadOptions(search)` only after the user
// types ≥ 2 characters (300 ms debounce), so it never pre-fetches the full
// table. Suitable for large reference datasets (thousands of ISCO codes, etc.).

export function InlineTypeahead({
  value, displayLabel, onSelect, loadOptions,
  placeholder, helper, allowClear, disabled,
}: {
  value: string
  displayLabel?: ReactNode
  /** Called when the user picks an option. The full InlineOption is passed so
   *  callers can read `.meta` for extra fields (e.g. title alongside a code). */
  onSelect: (opt: InlineOption) => Promise<void>
  loadOptions: (search: string) => Promise<InlineOption[]>
  placeholder?: string
  helper?: string
  allowClear?: boolean
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<InlineOption[]>([])
  const [loading, setLoading] = useState(false)

  // Debounced server fetch — only fires when ≥ 2 chars are typed.
  useEffect(() => {
    if (!open) return
    if (search.trim().length < 2) { setResults([]); return }
    const t = setTimeout(async () => {
      setLoading(true)
      try { setResults(await loadOptions(search.trim())) }
      catch { setResults([]) }
      finally { setLoading(false) }
    }, 280)
    return () => clearTimeout(t)
  }, [search, open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reset state on close.
  useEffect(() => { if (!open) { setSearch(''); setResults([]) } }, [open])

  async function pick(opt: InlineOption) {
    try { await onSelect(opt); toast.success('Saved'); setOpen(false) }
    catch (e) { toast.error(extractErr(e)) }
  }

  const shown = displayLabel ?? (
    value
      ? value
      : placeholder
        ? <span className="text-muted-foreground">{placeholder}</span>
        : <Dash />
  )

  if (disabled) return <span>{shown}</span>

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="text-left">
          <EditTrigger>{shown}</EditTrigger>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-2 border-b">
          <input
            autoFocus
            className="w-full text-sm bg-transparent outline-none placeholder:text-muted-foreground"
            placeholder="Type to search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {loading && (
            <p className="text-xs text-muted-foreground text-center py-4">Searching…</p>
          )}
          {!loading && search.trim().length < 2 && (
            <p className="text-xs text-muted-foreground text-center py-4">Type at least 2 characters to search</p>
          )}
          {!loading && search.trim().length >= 2 && results.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-4">No matches.</p>
          )}
          {allowClear && value && !loading && (
            <button
              type="button"
              onClick={() => pick({ value: '', label: '— none —' })}
              className="w-full text-left px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
            >— none —</button>
          )}
          {results.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => pick(opt)}
              className={
                'w-full text-left px-3 py-2 text-sm hover:bg-muted ' +
                (opt.value === value ? 'bg-muted/60 font-medium' : '')
              }
            >{opt.label}</button>
          ))}
        </div>
        {helper && <p className="text-xs text-muted-foreground px-3 py-2 border-t">{helper}</p>}
      </PopoverContent>
    </Popover>
  )
}

// ─────────────── InlineStatus ───────────────
// Colored chip that opens a small option list on click.

export function InlineStatus<T extends string>({
  value, options, colorMap, onSave, className,
}: {
  value: T
  options: { value: T; label: string }[]
  colorMap: Record<T, string>
  onSave: (v: T) => Promise<void>
  className?: string
}) {
  const [open, setOpen] = useState(false)
  async function save(next: T) {
    if (next === value) { setOpen(false); return }
    try { await onSave(next); toast.success('Saved'); setOpen(false) }
    catch (e) { toast.error(extractErr(e)) }
  }
  const label = options.find((o) => o.value === value)?.label || String(value)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`text-xs px-2 py-0.5 rounded font-semibold hover:opacity-80 ${colorMap[value]} ${className || ''}`}
        >{label}</button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-1" align="start">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => save(o.value)}
            className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-muted ${o.value === value ? 'bg-muted/60 font-semibold' : ''}`}
          >
            <span className={`inline-block w-2 h-2 rounded-full mr-2 align-middle ${colorMap[o.value].split(' ')[0]}`} />
            {o.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}

// ─────────────── RecordDrawer ───────────────
// Right-side Sheet with header + scrollable body + sticky footer. Used
// for creates and multi-field edits (where two or more fields interact).

export function RecordDrawer({
  open, onOpenChange, title, description, onSubmit, isPending,
  submitLabel = 'Save', width = 'default', children,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  title: string
  description?: string
  onSubmit: () => void
  isPending?: boolean
  submitLabel?: string
  width?: 'default' | 'wide'
  children: ReactNode
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={`flex flex-col p-0 ${width === 'wide' ? 'sm:max-w-[560px]' : 'sm:max-w-[480px]'}`}
      >
        <SheetHeader className="border-b">
          <SheetTitle>{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>
        <form
          onSubmit={(e: FormEvent) => { e.preventDefault(); onSubmit() }}
          className="flex-1 flex flex-col min-h-0"
        >
          <div className="flex-1 overflow-y-auto p-6">{children}</div>
          <SheetFooter className="border-t flex-row justify-end gap-2 sm:justify-end">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving…' : submitLabel}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

// ─────────────── useDeleteConfirm ───────────────
// Returns { confirm, dialog }. Render `dialog` once at the top of the
// component; call `confirm(label, onConfirm)` to open it.

export function useDeleteConfirm() {
  const [state, setState] = useState<{ open: boolean; label: string; onConfirm: () => void }>({
    open: false, label: '', onConfirm: () => {},
  })
  const confirm = (label: string, onConfirm: () => void) =>
    setState({ open: true, label, onConfirm })
  const dialog = (
    <AlertDialog open={state.open} onOpenChange={(v) => setState((s) => ({ ...s, open: v }))}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {state.label}?</AlertDialogTitle>
          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => { state.onConfirm(); setState((s) => ({ ...s, open: false })) }}
          >Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
  return { confirm, dialog }
}

// ─────────────── RowActions ───────────────
// Kebab menu (Edit / Delete) used at the end of every list row.

export function RowActions({ onEdit, onDelete }:
  { onEdit: () => void; onDelete: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onEdit}>Edit…</DropdownMenuItem>
        <DropdownMenuItem onClick={onDelete} className="text-destructive">Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
